/**
 * @file ExtractedInformationSkeleton.tsx
 *
 * @description This component renders a loading placeholder for the extracted information
 * section of the police records interface. It displays a skeleton UI that mimics the layout
 * of the actual information that will be displayed once data is loaded.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import { Box, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function ExtractedInformationSkeleton
 *
 * @description This component renders a placeholder loading state for extracted case information.
 * It displays skeleton elements that mimic the layout and structure of the actual data
 * that will be displayed when loaded. The skeleton provides visual feedback to officers
 * that data is being processed, improving perceived performance of the application.
 *
 * Design notes:
 * - Uses a light gray background with border to visually separate it from other content
 * - Responsive spacing and sizing based on viewport size
 * - Mimics typical document information layout with header and content sections
 *
 * Accessibility:
 * - Skeletons are recognized by screen readers as loading states
 *
 * @returns {JSX.Element} A skeleton UI component mimicking the extracted information layout
 */

export const ExtractedInformationSkeleton = () => {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
        my: 2,
        p: { xs: 2, sm: 3, md: 4 },
        textAlign: "left",
        width: "100%",
        maxWidth: 1400,
        mx: "auto",
      }}
    >
      {/* Main grid container */}
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} flexDirection="column">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 12 }} sx={{ mt: 1 }}>
            {/* Primary header skeleton */}
            <Skeleton height={60} width="100%" />
          </Grid>
        </Grid>
        {/* Middle section for main information */}
        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
          <Skeleton height={20} width="50%" sx={{ mb: 1 }} />
          <Skeleton height={60} width="100%" />
        </Grid>
        {/* Bottom section */}
        <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
          <Skeleton height={20} width="30%" sx={{ mb: 1 }} />
          <Skeleton height={100} width="100%" />
        </Grid>
      </Grid>
    </Box>
  );
};
